import { Toast as ToastPrimitive } from "@base-ui/react/toast";
import {
  IconAlertTriangleFilled,
  IconCircleCheckFilled,
  IconExclamationCircleFilled,
  IconInfoCircleFilled,
  IconX,
} from "@tabler/icons-react";
import type * as React from "react";

import { cn, cnState } from "@beweegbaas/ui/lib/utils";

// Module-level managers so `toast.*` works outside React (route loaders, middleware).
// Anchored toasts get their own manager/provider per the Base UI docs, so they never
// stack into the main viewport.
const toastManager = ToastPrimitive.createToastManager();
const anchoredToastManager = ToastPrimitive.createToastManager();

type AddOptions = Parameters<typeof toastManager.add>[0];
type MessageOptions = Omit<AddOptions, "title" | "type">;
type AnchoredOptions = Omit<AddOptions, "title" | "positionerProps"> &
  NonNullable<AddOptions["positionerProps"]>;

// Stable id per anchor element, so repeated triggers (spamming a copy button) upsert
// the same toast — refreshing its timer and replaying the pulse — instead of stacking.
const anchorIds = new WeakMap<Element, string>();
let nextAnchorId = 0;
function anchorToastId(anchor: Element | null | undefined): string | undefined {
  if (!anchor) return undefined;
  let id = anchorIds.get(anchor);
  if (id === undefined) {
    id = `anchored-toast-${nextAnchorId++}`;
    anchorIds.set(anchor, id);
  }
  return id;
}

// Alternating the animation name by updateKey parity replays the pulse on each upsert.
function pulseClass(updateKey: number | undefined): string | undefined {
  if (!updateKey) return undefined;
  return updateKey % 2 === 0 ? "animate-toast-pulse-even" : "animate-toast-pulse-odd";
}

const toast = {
  success: (message: React.ReactNode, options?: MessageOptions) =>
    toastManager.add({ ...options, title: message, type: "success" }),
  error: (message: React.ReactNode, options?: MessageOptions) =>
    toastManager.add({ ...options, title: message, type: "error" }),
  info: (message: React.ReactNode, options?: MessageOptions) =>
    toastManager.add({ ...options, title: message, type: "info" }),
  warning: (message: React.ReactNode, options?: MessageOptions) =>
    toastManager.add({ ...options, title: message, type: "warning" }),
  add: (options: AddOptions) => toastManager.add(options),
  update: toastManager.update.bind(toastManager),
  close: (toastId?: string) => toastManager.close(toastId),
  promise: toastManager.promise.bind(toastManager),
  /** Small contextual toast positioned against an element ("Gekopieerd" next to a button). */
  anchored: (
    message: React.ReactNode,
    {
      anchor,
      side,
      sideOffset = 8,
      align,
      alignOffset,
      showArrow = true,
      ...options
    }: AnchoredOptions & { showArrow?: boolean },
  ) =>
    anchoredToastManager.add({
      timeout: 2000,
      id: anchorToastId(anchor),
      ...options,
      data: { ...options.data, showArrow },
      description: message,
      positionerProps: { anchor, side, sideOffset, align, alignOffset },
    }),
};

const typeIcons: Record<string, React.ReactNode> = {
  success: <IconCircleCheckFilled className="text-success size-5" />,
  error: <IconExclamationCircleFilled className="text-destructive size-5" />,
  info: <IconInfoCircleFilled className="text-primary size-5" />,
  warning: <IconAlertTriangleFilled className="size-5 text-amber-500" />,
};

/**
 * Mounts both toast layers: the stacked top-center viewport and the anchored layer.
 * Render once, near the root.
 */
function Toaster({ timeout, limit }: { timeout?: number; limit?: number }) {
  return (
    <>
      <ToastPrimitive.Provider toastManager={toastManager} timeout={timeout} limit={limit}>
        <ToastPrimitive.Portal>
          <ToastPrimitive.Viewport
            data-slot="toast-viewport"
            className="fixed inset-x-0 top-4 z-100 mx-auto w-[calc(100vw-2rem)]
              sm:w-90"
          >
            <StackedToasts />
          </ToastPrimitive.Viewport>
        </ToastPrimitive.Portal>
      </ToastPrimitive.Provider>
      <ToastPrimitive.Provider toastManager={anchoredToastManager} limit={1}>
        <ToastPrimitive.Portal>
          <ToastPrimitive.Viewport data-slot="anchored-toast-viewport">
            <AnchoredToasts />
          </ToastPrimitive.Viewport>
        </ToastPrimitive.Portal>
      </ToastPrimitive.Provider>
    </>
  );
}

// Top-center adaptation of the Base UI stacked-toast demo: behind toasts peek out
// below the frontmost one, expanded toasts spread downward, enter/exit from above.
function StackedToasts() {
  const { toasts } = ToastPrimitive.useToastManager();
  return toasts.map((t) => (
    <ToastPrimitive.Root
      key={t.id}
      toast={t}
      swipeDirection={["up"]}
      data-slot="toast"
      className={cn(
        `[--gap:0.75rem] [--peek:0.625rem]
        [--scale:calc(max(0,1-(var(--toast-index)*0.1)))]
        [--shrink:calc(1-var(--scale))]
        [--height:var(--toast-frontmost-height,var(--toast-height))]
        [--offset-y:calc(var(--toast-offset-y)+(var(--toast-index)*var(--gap))+var(--toast-swipe-movement-y))]`,
        `absolute top-0 left-0 z-[calc(1000-var(--toast-index))] w-full
        origin-top select-none`,
        "h-(--height) data-expanded:h-(--toast-height)",
        `bg-popover text-popover-foreground ring-foreground/10 rounded-lg
        shadow-lg ring-1`,
        "[transition:transform_0.5s_cubic-bezier(0.22,1,0.36,1),opacity_0.5s,height_0.15s]",
        "[transform:translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)+(var(--toast-index)*var(--peek))+(var(--shrink)*var(--height))))_scale(var(--scale))]",
        "data-expanded:[transform:translateX(var(--toast-swipe-movement-x))_translateY(var(--offset-y))]",
        "data-starting-style:[transform:translateY(-150%)]",
        "data-ending-style:opacity-0 data-limited:opacity-0",
        "[&[data-ending-style]:not([data-limited]):not([data-swipe-direction])]:[transform:translateY(-150%)]",
        "data-ending-style:data-[swipe-direction=up]:[transform:translateY(calc(var(--toast-swipe-movement-y)-150%))]",
        "data-ending-style:data-[swipe-direction=down]:[transform:translateY(calc(var(--toast-swipe-movement-y)+150%))]",
        "data-ending-style:data-[swipe-direction=left]:[transform:translateX(calc(var(--toast-swipe-movement-x)-150%))_translateY(var(--offset-y))]",
        "data-ending-style:data-[swipe-direction=right]:[transform:translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))]",
        // Invisible bridge over the gap below, so hover isn't lost between expanded toasts.
        `after:absolute after:top-full after:left-0
        after:h-[calc(var(--gap)+1px)] after:w-full after:content-['']`,
        "focus-visible:outline-ring focus-visible:outline-2 focus-visible:-outline-offset-1",
        pulseClass(t.updateKey),
      )}
    >
      <ToastPrimitive.Content
        data-slot="toast-content"
        className="flex h-full items-center gap-3 overflow-hidden p-4
          [transition:opacity_0.25s_cubic-bezier(0.22,1,0.36,1)]
          data-behind:opacity-0 data-expanded:opacity-100"
      >
        {t.type !== undefined && typeIcons[t.type] !== undefined && (
          <span className="shrink-0">{typeIcons[t.type]}</span>
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          {/* Renders an h2 — m-0/font-sans shield it from app-level heading base styles. */}
          <ToastPrimitive.Title
            data-slot="toast-title"
            className="m-0 font-sans text-sm font-medium"
          />
          <ToastPrimitive.Description
            data-slot="toast-description"
            className="text-muted-foreground m-0 text-sm"
          />
        </div>
        {t.actionProps && (
          <ToastPrimitive.Action
            data-slot="toast-action"
            className="hover:bg-muted shrink-0 rounded-md px-2 py-1 text-sm
              font-medium"
          />
        )}
        <ToastPrimitive.Close
          data-slot="toast-close"
          aria-label="Sluiten"
          className="text-muted-foreground hover:text-foreground hover:bg-muted
            flex size-6 shrink-0 items-center justify-center rounded-md"
        >
          <IconX className="size-4" />
        </ToastPrimitive.Close>
      </ToastPrimitive.Content>
    </ToastPrimitive.Root>
  ));
}

function AnchoredToasts() {
  const { toasts } = ToastPrimitive.useToastManager();
  return toasts.map((t) => (
    // Positioner reads t.positionerProps (anchor, side, offsets, arrowPadding…) itself.
    <ToastPrimitive.Positioner key={t.id} toast={t} className="isolate z-100">
      <ToastPrimitive.Root
        toast={t}
        data-slot="anchored-toast"
        className={cn(
          `bg-popover text-popover-foreground ring-foreground/10 rounded-md
          px-3 py-1.5 shadow-lg ring-1
          [transition:opacity_0.15s,scale_0.15s]
          data-starting-style:scale-95 data-starting-style:opacity-0
          data-ending-style:scale-95 data-ending-style:opacity-0`,
          pulseClass(t.updateKey),
        )}
      >
        {(t.data as { showArrow?: boolean } | undefined)?.showArrow !== false && <ToastArrow />}
        <ToastPrimitive.Content data-slot="anchored-toast-content">
          <ToastPrimitive.Description className="text-sm" />
        </ToastPrimitive.Content>
      </ToastPrimitive.Root>
    </ToastPrimitive.Positioner>
  ));
}

// Same triangle as PopoverArrow (popover.tsx) — Toast.Arrow exposes the identical
// data-side contract, but it's a different primitive so the SVG is duplicated here.
function ToastArrow({ className, ...props }: ToastPrimitive.Arrow.Props) {
  return (
    <ToastPrimitive.Arrow
      data-slot="anchored-toast-arrow"
      className={cnState(
        `fill-popover [&>svg]:fill-popover [&>svg]:stroke-border
        data-[side=bottom]:bottom-full data-[side=left]:left-full
        data-[side=right]:right-full data-[side=top]:top-full
        data-[side=bottom]:[&>svg]:rotate-180 data-[side=left]:[&>svg]:-rotate-90
        data-[side=right]:[&>svg]:rotate-90 data-[side=top]:[&>svg]:rotate-0`,
        className,
      )}
      {...props}
    >
      <svg width="12" height="12" viewBox="0 0 12 12" className="block transition-transform">
        <path d="M0 0 L6 6 L12 0" className="fill-inherit stroke-inherit" />
      </svg>
    </ToastPrimitive.Arrow>
  );
}

// Export ToastPrimitive for advanced use cases
export { toast, Toaster, ToastPrimitive };
